import { buildSceneRenderPlan, type InvivoSceneAnatomyNodePlan } from "./build_scene_render_plan";
import type { InvivoScenePackage } from "../scene/invivo_scene_package";
import { createInvivoMaterial } from "./materials/create_invivo_material";
import { loadInvivoModel } from "./load_invivo_model";
import { configureBackgroundRuntime } from "./background_runtime";
import { applyRenderSettingsRuntime } from "./render_settings_runtime";
import {
  applySkinTranslucencySetting,
  DEFAULT_SKIN_TRANSLUCENCY,
  rememberSkinMeshGeometry,
  type SkinRuntimeMesh,
  type SkinTranslucencySetting,
  updateSkinFacetDepthSorting,
} from "./skin_runtime";

function degreesToRadians(value: number): number {
  return (value * Math.PI) / 180;
}

function yawToArcRotateAlpha(yawDegrees: number): number {
  return degreesToRadians(yawDegrees) + Math.PI / 2;
}

function pitchToArcRotateBeta(pitchDegrees: number): number {
  return Math.PI / 2 - degreesToRadians(pitchDegrees);
}

const ANATOMY_WHEEL_ZOOM_PRECISION = 240;
const ANATOMY_PINCH_ZOOM_PRECISION = 360;
const ANATOMY_PANNING_SENSIBILITY = 1800;

function snapshotMaterial(material: any): Record<string, unknown> {
  return {
    name: material?.name,
    alpha: material?.alpha,
    backFaceCulling: material?.backFaceCulling,
    transparencyMode: material?.transparencyMode,
    baseColor: material?.baseColor,
    metallic: material?.metallic,
    roughness: material?.roughness,
    baseTextureUrl: material?.baseTexture?.url,
    normalTextureUrl: material?.normalTexture?.url,
    metallicRoughnessTextureUrl: material?.metallicRoughnessTexture?.url,
    occlusionTextureUrl: material?.occlusionTexture?.url || material?.ambientTexture?.url,
  };
}

function snapshotLight(light: any): Record<string, unknown> {
  return {
    name: light?.name,
    intensity: light?.intensity,
    direction: light?.direction,
  };
}

function hasRenderableGeometry(mesh: any): boolean {
  if (typeof mesh?.getTotalVertices === "function") {
    return mesh.getTotalVertices() > 0;
  }

  if ("geometry" in mesh) {
    return Boolean(mesh.geometry);
  }

  return true;
}

function requireMaterialPlan({
  materialPlans,
  materialName,
}: {
  materialPlans: Map<string, unknown>;
  materialName: string;
}): unknown {
  const materialPlan = materialPlans.get(materialName);

  if (!materialPlan) {
    throw new Error(`Missing normalized material plan for "${materialName}".`);
  }

  return materialPlan;
}

function cloneRuntimeAnatomyTree(nodes: InvivoSceneAnatomyNodePlan[]): InvivoRuntimeAnatomyNode[] {
  return nodes.map((node) => ({
    id: node.id,
    label: node.label,
    meshes: [...node.meshes],
    ownMeshes: [...node.ownMeshes],
    visible: node.visible,
    variant: node.variant,
    defaultVariant: node.defaultVariant,
    complication: node.complication,
    children: cloneRuntimeAnatomyTree(node.children),
  }));
}

function forEachAnatomyNode(nodes: InvivoSceneAnatomyNodePlan[], visitor: (node: InvivoSceneAnatomyNodePlan) => void): void {
  nodes.forEach((node) => {
    visitor(node);
    forEachAnatomyNode(node.children, visitor);
  });
}

function forEachRuntimeNode(nodes: InvivoRuntimeAnatomyNode[], visitor: (node: InvivoRuntimeAnatomyNode) => void): void {
  nodes.forEach((node) => {
    visitor(node);
    forEachRuntimeNode(node.children, visitor);
  });
}

function getCanvasPoint(canvas: HTMLCanvasElement, event: PointerEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect?.() || { left: 0, top: 0, width: canvas.width || 1, height: canvas.height || 1 };
  const scaleX = (canvas.width || rect.width || 1) / (rect.width || 1);
  const scaleY = (canvas.height || rect.height || 1) / (rect.height || 1);

  return {
    x: (event.clientX - rect.left) * scaleX,
    y: (event.clientY - rect.top) * scaleY,
  };
}

function getViewportPoint(canvas: HTMLCanvasElement, event: PointerEvent): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect?.() || { left: 0, top: 0 };

  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
}

export interface InvivoRuntimeAnatomyNode {
  id: string;
  label: string;
  meshes: string[];
  ownMeshes: string[];
  visible: boolean;
  variant: boolean;
  defaultVariant: boolean;
  complication: boolean | string | null;
  children: InvivoRuntimeAnatomyNode[];
}

export interface InvivoRuntimeHandle {
  anatomyTree: InvivoRuntimeAnatomyNode[];
  anatomyGroups: InvivoRuntimeAnatomyNode[];
  dispose(): void;
  setSkinTranslucency(setting: SkinTranslucencySetting): void;
  setDrapesVisible(visible: boolean): void;
  setAnatomyGroupVisible(id: string, visible: boolean): void;
  setAnatomyNodeVisible(id: string, visible: boolean): void;
}

export async function createBabylonScene({
  canvas,
  scenePackage,
}: {
  canvas: HTMLCanvasElement;
  scenePackage: InvivoScenePackage;
}): Promise<InvivoRuntimeHandle> {
  const { BABYLON } = window;
  const invivoScenePackage = scenePackage;
  const renderPlan = buildSceneRenderPlan(invivoScenePackage.definition as any);
  const engine = new BABYLON.Engine(canvas, true, { preserveDrawingBuffer: true, stencil: true });
  const scene = new BABYLON.Scene(engine);
  const materialCache = new Map<string, unknown>();
  const cameraDefinition = (invivoScenePackage.definition.camera || {}) as Record<string, number>;
  const workspaceDefinition = (invivoScenePackage.definition.workspace || {}) as Record<string, number>;
  const skinMeshes: SkinRuntimeMesh[] = [];
  const createdLights: unknown[] = [];
  const materialDiagnostics = {
    plans: Object.fromEntries(invivoScenePackage.materialPlans.entries()),
    created: {} as Record<string, unknown>,
    meshAssignments: {} as Record<string, unknown>,
  };
  const lightingSettings = invivoScenePackage.lightingSettings || {
    hemisphericLightEnabled: true,
    environmentFollowsCamera: false,
  };
  const lightingDiagnostics: Record<string, unknown> = {
    settings: lightingSettings,
    lights: [],
    environmentTexturePresent: false,
    backgroundMode: invivoScenePackage.background.mode,
    backgroundSphereTransform: null,
    lastEnvironmentMatrix: null,
  };
  let skinTranslucencySetting = DEFAULT_SKIN_TRANSLUCENCY;
  const drapeMeshes: Array<{
    name?: string;
    isVisible?: boolean;
    setEnabled?(enabled: boolean): void;
  }> = [];
  const meshByName = new Map<string, {
    name?: string;
    isVisible?: boolean;
    setEnabled?(enabled: boolean): void;
  }>();
  const longPressPopupParent = canvas.parentElement;
  let longPressTimer: ReturnType<typeof window.setTimeout> | null = null;
  let longPressStart: { x: number; y: number; pointerId: number } | null = null;
  let longPressPopup: HTMLElement | null = null;

  scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);
  scene.autoClear = false;
  scene.skipPointerMovePicking = true;
  scene.useOrderIndependentTransparency = false;

  const backgroundSphere = BABYLON.MeshBuilder?.CreateSphere(
    "Invivo::background",
    {
      segments: 32,
      diameter: 10,
      sideOrientation: 1,
    },
    scene
  );

  if (backgroundSphere?.position) {
    backgroundSphere.position.y = 0;
  }

  if (backgroundSphere) {
    backgroundSphere.receiveShadows = true;
    backgroundSphere.infiniteDistance = true;
    backgroundSphere.isPickable = false;
    if (backgroundSphere.rotation) {
      backgroundSphere.rotation.x = Math.PI / 2;
    }
  }

  const camera = new BABYLON.ArcRotateCamera(
    "Invivo::camera",
    yawToArcRotateAlpha(cameraDefinition.yaw ?? 180),
    pitchToArcRotateBeta(cameraDefinition.pitch ?? 90),
    cameraDefinition.range ?? 0.7,
    new BABYLON.Vector3(
      (cameraDefinition.x || 0) - (workspaceDefinition.x || 0),
      (cameraDefinition.y || 0) - (workspaceDefinition.y || 0),
      (cameraDefinition.z || 0) - (workspaceDefinition.z || 0)
    ),
    scene
  );
  camera.fovMode = BABYLON.Camera.FOVMODE_HORIZONTAL_FIXED;
  camera.inertia = 0.8;
  camera.wheelPrecision = ANATOMY_WHEEL_ZOOM_PRECISION;
  camera.pinchPrecision = ANATOMY_PINCH_ZOOM_PRECISION;
  camera.panningSensibility = ANATOMY_PANNING_SENSIBILITY;
  camera.minZ = 0.01;
  camera.maxZ = 5;
  camera.lowerRadiusLimit = 0.04;
  camera.upperRadiusLimit = 5;
  camera.lowerBetaLimit = 0.05;
  camera.upperBetaLimit = Math.PI - 0.05;
  scene.activeCamera = camera;
  camera.attachControl(canvas, true);

  if (lightingSettings.hemisphericLightEnabled) {
    createdLights.push(new BABYLON.HemisphericLight("Invivo::light", new BABYLON.Vector3(0, 1, 0), scene));
  }

  const updateEnvironmentReflectionMatrix = (): void => {
    const environmentTexture = scene.environmentTexture as any;
    if (!lightingSettings.environmentFollowsCamera || !environmentTexture?.setReflectionTextureMatrix || !BABYLON.Matrix?.RotationYawPitchRoll) {
      return;
    }

    const matrix = BABYLON.Matrix.RotationYawPitchRoll(
      camera.alpha + (lightingSettings.environmentYawOffset || 0),
      camera.beta + (lightingSettings.environmentPitchOffset || 0),
      lightingSettings.environmentRollOffset || 0
    );
    environmentTexture.setReflectionTextureMatrix(matrix);
    lightingDiagnostics.lastEnvironmentMatrix = matrix;
  };

  scene.registerBeforeRender?.(() => {
    updateEnvironmentReflectionMatrix();
    if (skinTranslucencySetting !== "translucent") {
      return;
    }

    updateSkinFacetDepthSorting({ cameraPosition: camera.position, skinMeshes });
  });

  applyRenderSettingsRuntime({ scene, camera, renderSettings: invivoScenePackage.renderSettings });

  const background = configureBackgroundRuntime({
    scene,
    background: invivoScenePackage.background,
  });

  if (backgroundSphere) {
    backgroundSphere.material = background.material;
    backgroundSphere.setEnabled?.(background.enabled);
  }
  updateEnvironmentReflectionMatrix();
  lightingDiagnostics.environmentTexturePresent = Boolean(scene.environmentTexture);
  lightingDiagnostics.lights = createdLights.map(snapshotLight);
  lightingDiagnostics.backgroundSphereTransform = backgroundSphere
    ? { position: backgroundSphere.position, rotation: backgroundSphere.rotation }
    : null;

  const sceneRoot = new BABYLON.TransformNode("Invivo::scene_root", scene);
  const workspaceRoot = new BABYLON.TransformNode("Invivo::workspace_root", scene);
  workspaceRoot.setParent(sceneRoot);
  workspaceRoot.position.x = workspaceDefinition.x || 0;
  workspaceRoot.position.y = workspaceDefinition.y || 0;
  workspaceRoot.position.z = workspaceDefinition.z || 0;
  workspaceRoot.scaling.x = renderPlan.rootTransform.scale.x;
  workspaceRoot.scaling.y = renderPlan.rootTransform.scale.y;
  workspaceRoot.scaling.z = renderPlan.rootTransform.scale.z;
  sceneRoot.rotation = new BABYLON.Vector3(
    degreesToRadians(renderPlan.rootTransform.rotation.x),
    degreesToRadians(renderPlan.rootTransform.rotation.y),
    degreesToRadians(renderPlan.rootTransform.rotation.z)
  );

  const meshes = await loadInvivoModel({
    scene,
    modelUrl: invivoScenePackage.modelUrl,
    workspaceRoot,
  });

  meshes.forEach((mesh) => {
    mesh.useVertexColors = false;
    mesh.hasVertexAlpha = false;
    mesh.visibility = 1;
    mesh.renderingGroupId = 1;

    if (!mesh.name) {
      return;
    }

    meshByName.set(mesh.name, mesh);

    const materialName = renderPlan.meshMaterialAssignments[mesh.name];

    if (!materialName) {
      if (hasRenderableGeometry(mesh)) {
        mesh.setEnabled?.(false);
        mesh.isVisible = false;
      }
      materialDiagnostics.meshAssignments[mesh.name] = null;
      return;
    }

    if (!materialCache.has(materialName)) {
      const materialPlan = requireMaterialPlan({
        materialPlans: invivoScenePackage.materialPlans,
        materialName,
      });

      const material = createInvivoMaterial({ scene, materialPlan: materialPlan as any });
      materialCache.set(materialName, material);
      materialDiagnostics.created[materialName] = snapshotMaterial(material);
    }

    mesh.material = materialCache.get(materialName);
    materialDiagnostics.meshAssignments[mesh.name] = materialName;

    const meshTransform = renderPlan.meshTransforms[mesh.name];

    if (meshTransform?.scale && mesh.scaling) {
      mesh.scaling.x = meshTransform.scale.x;
      mesh.scaling.y = meshTransform.scale.y;
      mesh.scaling.z = meshTransform.scale.z;
    }

    if (meshTransform?.translation && mesh.position) {
      mesh.position.x = meshTransform.translation.x;
      mesh.position.y = meshTransform.translation.y;
      mesh.position.z = meshTransform.translation.z;
    }

    if (meshTransform?.rotation && mesh.rotation) {
      mesh.rotation.x = degreesToRadians(meshTransform.rotation.x);
      mesh.rotation.y = degreesToRadians(meshTransform.rotation.y);
      mesh.rotation.z = degreesToRadians(meshTransform.rotation.z);
    }

    if (meshTransform?.uvScale && mesh.getVerticesData && mesh.updateVerticesData) {
      const uvData = mesh.getVerticesData("uv");

      if (uvData) {
        const scaledUvData = Array.from(uvData);

        for (let index = 0; index < scaledUvData.length; index += 2) {
          scaledUvData[index] *= meshTransform.uvScale.u;
          scaledUvData[index + 1] *= meshTransform.uvScale.v;
        }

        mesh.updateVerticesData("uv", scaledUvData);
      }
    }

    if (renderPlan.groups.skin.includes(mesh.name)) {
      rememberSkinMeshGeometry(mesh);
      skinMeshes.push(mesh);
    }

    if (renderPlan.groups.sheet.includes(mesh.name)) {
      drapeMeshes.push(mesh);
    }
  });

  const applyDrapeRenderingMode = (): void => {
    const renderingGroupId = skinTranslucencySetting === "outlines" ? 0 : 1;

    drapeMeshes.forEach((mesh) => {
      (mesh as { renderingGroupId?: number }).renderingGroupId = renderingGroupId;
    });
  };

  const setSkinTranslucency = (setting: SkinTranslucencySetting): void => {
    skinTranslucencySetting = setting;
    applySkinTranslucencySetting({ BABYLON, skinMeshes, setting });
    applyDrapeRenderingMode();
  };

  const anatomyTree = cloneRuntimeAnatomyTree(renderPlan.anatomyTree);
  const anatomyGroups = anatomyTree;
  const anatomyGroupVisibility = new Map<string, boolean>();
  const anatomyNodeById = new Map<string, InvivoSceneAnatomyNodePlan>();

  const anatomyNodeByMeshName = new Map<string, InvivoSceneAnatomyNodePlan>();

  forEachAnatomyNode(renderPlan.anatomyTree, (node) => {
    anatomyNodeById.set(node.id, node);
    anatomyGroupVisibility.set(node.id, node.visible);
    node.ownMeshes.forEach((meshName) => {
      anatomyNodeByMeshName.set(meshName, node);
    });
  });

  const setDrapesVisible = (visible: boolean): void => {
    drapeMeshes.forEach((mesh) => {
      mesh.setEnabled?.(visible);
      mesh.isVisible = visible;
    });
    applyDrapeRenderingMode();
  };

  const setRuntimeSubtreeVisible = (nodes: InvivoRuntimeAnatomyNode[], id: string, visible: boolean): boolean => {
    for (const node of nodes) {
      if (node.id === id) {
        forEachRuntimeNode([node], (candidate) => {
          candidate.visible = visible;
        });
        return true;
      }

      if (setRuntimeSubtreeVisible(node.children, id, visible)) {
        return true;
      }
    }

    return false;
  };

  const setAnatomyNodeVisible = (id: string, visible: boolean): void => {
    const node = anatomyNodeById.get(id);

    if (!node) {
      return;
    }

    forEachAnatomyNode([node], (candidate) => {
      anatomyGroupVisibility.set(candidate.id, visible);
    });
    setRuntimeSubtreeVisible(anatomyTree, id, visible);
    node.meshes.forEach((meshName) => {
      const mesh = meshByName.get(meshName);

      mesh?.setEnabled?.(visible);
      if (mesh) {
        mesh.isVisible = visible;
      }
    });
  };

  const setAnatomyGroupVisible = setAnatomyNodeVisible;

  const closeLongPressPopup = (): void => {
    longPressPopup?.remove();
    longPressPopup = null;
  };

  const collectLongPressCandidates = (point: { x: number; y: number }): InvivoSceneAnatomyNodePlan[] => {
    const skinMeshNames = new Set(renderPlan.groups.skin);
    const drapeMeshNames = new Set(renderPlan.groups.sheet);
    const samples = [
      [0, 0],
      [-10, 0],
      [10, 0],
      [0, -10],
      [0, 10],
      [-8, -8],
      [8, -8],
      [-8, 8],
      [8, 8],
      [-16, 0],
      [16, 0],
      [0, -16],
      [0, 16],
    ];
    const rankedNodes = new Map<string, { node: InvivoSceneAnatomyNodePlan; score: number }>();

    const addMeshCandidate = (mesh: any, score: number): void => {
      const meshName = mesh?.name;

      if (!meshName || skinMeshNames.has(meshName) || drapeMeshNames.has(meshName)) {
        return;
      }

      if (mesh.isVisible === false || mesh.isEnabled?.() === false) {
        return;
      }

      const node = anatomyNodeByMeshName.get(meshName);

      if (!node || anatomyGroupVisibility.get(node.id) === false) {
        return;
      }

      const previous = rankedNodes.get(node.id);

      if (!previous || score < previous.score) {
        rankedNodes.set(node.id, { node, score });
      }
    };

    samples.forEach(([offsetX, offsetY]) => {
      const x = point.x + offsetX;
      const y = point.y + offsetY;
      const score = Math.hypot(offsetX, offsetY);
      const hits = scene.multiPick?.(x, y) || [];

      if (hits.length) {
        hits.forEach((hit: any) => {
          addMeshCandidate(hit?.pickedMesh, score);
        });
        return;
      }

      addMeshCandidate(scene.pick?.(x, y)?.pickedMesh, score);
    });

    return [...rankedNodes.values()]
      .sort((left, right) => left.score - right.score || left.node.label.localeCompare(right.node.label))
      .slice(0, 5)
      .map((entry) => entry.node);
  };

  const showLongPressPopup = ({
    point,
    candidates,
  }: {
    point: { x: number; y: number };
    candidates: InvivoSceneAnatomyNodePlan[];
  }): void => {
    closeLongPressPopup();

    if (!longPressPopupParent || candidates.length === 0) {
      return;
    }

    const popup = document.createElement("div");
    popup.className = "invivo-pick-menu";
    popup.setAttribute("role", "menu");

    const title = document.createElement("div");
    title.className = "invivo-pick-menu__title";
    title.textContent = "Hide structure";
    popup.append(title);

    candidates.forEach((node) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "invivo-pick-menu__item";
      button.textContent = node.label;
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        setAnatomyNodeVisible(node.id, false);
        closeLongPressPopup();
      });
      popup.append(button);
    });

    longPressPopupParent.append(popup);

    const popupRect = popup.getBoundingClientRect();
    const parentRect = longPressPopupParent.getBoundingClientRect();
    const left = Math.min(Math.max(point.x - popupRect.width / 2, 10), Math.max(parentRect.width - popupRect.width - 10, 10));
    const top = Math.min(Math.max(point.y - popupRect.height - 14, 10), Math.max(parentRect.height - popupRect.height - 10, 10));

    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;
    longPressPopup = popup;
  };

  const clearLongPressTimer = (): void => {
    if (longPressTimer) {
      window.clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  };

  const pointerDownHandler = (event: PointerEvent): void => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    closeLongPressPopup();
    longPressStart = { x: event.clientX, y: event.clientY, pointerId: event.pointerId };
    const canvasPoint = getCanvasPoint(canvas, event);
    const viewportPoint = getViewportPoint(canvas, event);

    clearLongPressTimer();
    longPressTimer = window.setTimeout(() => {
      longPressTimer = null;
      const candidates = collectLongPressCandidates(canvasPoint);
      showLongPressPopup({ point: viewportPoint, candidates });
    }, 550);
  };

  const pointerMoveHandler = (event: PointerEvent): void => {
    if (!longPressStart || event.pointerId !== longPressStart.pointerId) {
      return;
    }

    if (Math.hypot(event.clientX - longPressStart.x, event.clientY - longPressStart.y) > 10) {
      clearLongPressTimer();
    }
  };

  const pointerEndHandler = (): void => {
    clearLongPressTimer();
    longPressStart = null;
  };

  const documentPointerDownHandler = (event: PointerEvent): void => {
    const target = event.target;

    if (target instanceof Node && longPressPopup?.contains(target)) {
      return;
    }

    closeLongPressPopup();
  };

  const contextMenuHandler = (event: Event): void => {
    event.preventDefault();
  };

  setSkinTranslucency(skinTranslucencySetting);
  setDrapesVisible(true);
  forEachAnatomyNode(renderPlan.anatomyTree, (node) => {
    setAnatomyNodeVisible(node.id, node.visible);
  });

  canvas.addEventListener?.("pointerdown", pointerDownHandler);
  canvas.addEventListener?.("pointermove", pointerMoveHandler);
  canvas.addEventListener?.("pointerup", pointerEndHandler);
  canvas.addEventListener?.("pointercancel", pointerEndHandler);
  canvas.addEventListener?.("contextmenu", contextMenuHandler);
  globalThis.document?.addEventListener("pointerdown", documentPointerDownHandler);

  const resizeHandler = (): void => {
    engine.resize();
  };

  (window as Window & { __invivoDebug?: Record<string, unknown> }).__invivoDebug = {
    scene,
    camera,
    meshes,
    backgroundSphere,
    drapeMeshes,
    skinMeshes,
    renderPlan,
    invivoScenePackage,
    materialDiagnostics,
    lightingDiagnostics,
    setSkinTranslucency,
    setDrapesVisible,
    setAnatomyGroupVisible,
    setAnatomyNodeVisible,
    anatomyTree,
    anatomyGroups,
    anatomyGroupVisibility,
  };

  engine.runRenderLoop(() => {
    scene.render();
  });
  window.addEventListener("resize", resizeHandler);

  return {
    anatomyTree,
    anatomyGroups,
    setSkinTranslucency,
    setDrapesVisible,
    setAnatomyGroupVisible,
    setAnatomyNodeVisible,
    dispose(): void {
      clearLongPressTimer();
      closeLongPressPopup();
      canvas.removeEventListener?.("pointerdown", pointerDownHandler);
      canvas.removeEventListener?.("pointermove", pointerMoveHandler);
      canvas.removeEventListener?.("pointerup", pointerEndHandler);
      canvas.removeEventListener?.("pointercancel", pointerEndHandler);
      canvas.removeEventListener?.("contextmenu", contextMenuHandler);
      globalThis.document?.removeEventListener("pointerdown", documentPointerDownHandler);
      window.removeEventListener("resize", resizeHandler);
      scene.dispose();
      engine.dispose();
    },
  };
}
